import React from "react";
import { Box, Heading, Columns, Column } from "@origin-digital/ods-core";
import {
  ArticleSkeleton,
  ArticleTitleSkeleton,
} from "../../../../components/Articles";
import { ResponsiveTabs } from "../../../../components/ResponsiveTabs";
import type { ResponsiveTabItem } from "../../../../components/ResponsiveTabs";
import { SubcategoryArticlesTab } from "../../components/SubcategoryArticlesTab";
import { CategoryFallback } from "../../components/CategoryFallback";
import { useArticleTabState } from "../../hooks/useArticleTabState";
import type { CategoryChild } from "../../../../services/categories/types";

interface ArticlesSectionProps {
  subcategories: CategoryChild[];
  loading: boolean;
  error: Error | null;
}

const buildTabItems = (
  subcategories: CategoryChild[],
  visited: Set<string>
): ResponsiveTabItem[] =>
  subcategories.map((child) => ({
    id: child.slug,
    label: child.name,
    subtitle: `${child.articleCount} ${
      child.articleCount === 1 ? "article" : "articles"
    }`,
    content: (
      <SubcategoryArticlesTab
        id={child.id}
        enabled={visited.has(String(child.id))}
      />
    ),
  }));

const ArticlesLoadingSkeleton: React.FC = () => (
  <Columns space="medium" collapseBelow="sm">
    <Column width={{ xs: "content", lg: "1/4" }}>
      <ArticleTitleSkeleton />
    </Column>
    <Column width={{ xs: "content", lg: "3/4" }}>
      <ArticleSkeleton />
    </Column>
  </Columns>
);

const SectionHeading: React.FC = () => (
  <Box marginBottom="large">
    <Heading component="h2" variant="h4">
      Search help article by topic
    </Heading>
  </Box>
);

export const ArticlesSection: React.FC<ArticlesSectionProps> = ({
  subcategories,
  loading,
  error,
}) => {
  const { activeId, visited, handleTabChange } =
    useArticleTabState(subcategories);
  const tabItems = buildTabItems(subcategories, visited);
  const showFallback = !loading && (error || tabItems.length === 0);
  const showTabs = !loading && !error && tabItems.length > 0;

  return (
    <Box
      marginY={{ xs: "xxlarge", lg: "xxxlarge" }}
      data-id="articles-section"
      marginX={{ xs: "medium", sm: "none" }}
    >
      <SectionHeading />
      {loading && <ArticlesLoadingSkeleton />}
      {showFallback && <CategoryFallback />}
      {showTabs && (
        <ResponsiveTabs
          items={tabItems}
          value={activeId}
          onChange={handleTabChange}
          aria-label="Sub-categories"
          data-id="category-page-tabs"
        />
      )}
    </Box>
  );
};
